import React, { useEffect, useState, useMemo, forwardRef } from "react";
import axios from "axios";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BASE_URL from "BASE_URL";

import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Box,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Avatar,
  Slide,
  Grow,
  Zoom,
  Fade,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { keyframes } from "@mui/system";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import NewsIcon from "@mui/icons-material/Newspaper";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionIcon from "@mui/icons-material/Description";
import TitleIcon from "@mui/icons-material/Title";
import { useNavigate } from "react-router-dom";

// Premium Animations
const slideUp = keyframes`
  from { transform: translateY(100px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(33, 150, 243, 0); }
  100% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.2); }
  50% { box-shadow: 0 0 25px rgba(33, 150, 243, 0.6); }
  100% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.2); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const bounceIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  70% { transform: scale(0.9); }
  100% { transform: scale(1); opacity: 1; }
`;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

// Transition component
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

function NewsManagement() {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteNewsTitle, setDeleteNewsTitle] = useState("");
  const [formMode, setFormMode] = useState("add");
  const [selectedId, setSelectedId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Filter states
  const [filterTitle, setFilterTitle] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Form states
  const [formData, setFormData] = useState({
    title: "",
    sort_description: "",
    description: "",
    status: "active",
    author: "",
    reference_by: "",
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState("");

  const token = localStorage.getItem("token");
  const metaBlue = "#1565C0";

  // Fetch all news
  const fetchNews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/news/display-all-news`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setNews(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch news", "error");
      }
    } catch (error) {
      console.error("Error fetching news:", error);
      showSnackbar(error.response?.data?.message || "Error fetching news", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
      if (!validTypes.includes(file.type)) {
        showSnackbar("Please select a valid image (JPEG, PNG, WEBP)", "warning");
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        showSnackbar("Image size should be less than 2MB", "warning");
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Handle form dialog open
  const handleOpenDialog = (mode = "add", item = null) => {
    setFormMode(mode);
    if (mode === "edit" && item) {
      setSelectedId(item._id);
      setFormData({
        title: item.title || "",
        sort_description: item.sort_description || "",
        description: item.description || "",
        status: item.status || "active",
        author: item.author || "",
        reference_by: item.reference_by || "",
      });
      setExistingImage(item.image || "");
      setImagePreview(item.image || null);
      setSelectedImage(null);
    } else {
      resetForm();
    }
    setOpenForm(true);
  };

  // Handle view dialog open
  const handleViewDialog = (item) => {
    setSelectedNews(item);
    setViewDialogOpen(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: "",
      sort_description: "",
      description: "",
      status: "active",
      author: "",
      reference_by: "",
    });
    setSelectedImage(null);
    setImagePreview(null);
    setExistingImage("");
    setSelectedId(null);
  };

  // Handle form submit
  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      showSnackbar("Title is required", "warning");
      return;
    }
    if (!formData.sort_description.trim()) {
      showSnackbar("Short description is required", "warning");
      return;
    }
    if (!formData.description.trim()) {
      showSnackbar("Description is required", "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("sort_description", formData.sort_description);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("status", formData.status);
      formDataToSend.append("author", formData.author || "Admin");
      formDataToSend.append("reference_by", formData.reference_by || "");

      if (selectedImage) {
        formDataToSend.append("image", selectedImage);
      }

      if (formMode === "add") {
        response = await axios.post(`${BASE_URL}/news/create-news`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        response = await axios.put(
          `${BASE_URL}/news/update-news?id=${selectedId}`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      if (response.data.success) {
        showSnackbar(`News ${formMode === "add" ? "added" : "updated"} successfully!`, "success");
        fetchNews();
        setOpenForm(false);
        resetForm();
      } else {
        showSnackbar(response.data.message || "Operation failed", "error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showSnackbar(error.response?.data?.message || "Operation failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete dialog open
  const handleOpenDeleteDialog = (id, title) => {
    setDeleteId(id);
    setDeleteNewsTitle(title);
    setDeleteDialogOpen(true);
  };

  // Handle delete news
  const handleDeleteNews = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.delete(`${BASE_URL}/news/delete-news?id=${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        showSnackbar("News deleted successfully!", "success");
        fetchNews();
        setDeleteDialogOpen(false);
      } else {
        showSnackbar(response.data.message || "Delete failed", "error");
      }
    } catch (error) {
      console.error("Error deleting news:", error);
      showSnackbar(error.response?.data?.message || "Failed to delete news", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Clear filters
  const clearFilters = () => {
    setFilterTitle("");
    setFilterStatus("");
  };

  // Filtered news
  const filteredNews = useMemo(() => {
    let filtered = [...news];

    if (filterTitle) {
      filtered = filtered.filter((item) =>
        item.title.toLowerCase().includes(filterTitle.toLowerCase())
      );
    }

    if (filterStatus) {
      filtered = filtered.filter((item) => item.status === filterStatus);
    }

    return filtered;
  }, [news, filterTitle, filterStatus]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status chip
  const getStatusChip = (status) => {
    return status === "active" ? (
      <Chip
        label="ACTIVE"
        size="small"
        sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: "bold" }}
      />
    ) : (
      <Chip
        label="INACTIVE"
        size="small"
        sx={{ bgcolor: "#ffebee", color: "#d32f2f", fontWeight: "bold" }}
      />
    );
  };

  const columns = [
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          IMAGE
        </MDTypography>
      ),
      accessor: "image",
      align: "center",
      //   width: "8%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          TITLE
        </MDTypography>
      ),
      accessor: "title",
      align: "left",
      //   width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          AUTHOR
        </MDTypography>
      ),
      accessor: "author",
      align: "center",
      //   width: "20%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          REFERENCE BY
        </MDTypography>
      ),
      accessor: "reference_by",
      align: "center",
      //   width: "10%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          STATUS
        </MDTypography>
      ),
      accessor: "status",
      align: "center",
      //   width: "8%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          CREATED
        </MDTypography>
      ),
      accessor: "createdAt",
      align: "center",
      //   width: "12%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          ACTIONS
        </MDTypography>
      ),
      accessor: "action",
      align: "center",
      //   width: "12%",
    },
  ];

  const rows = useMemo(
    () =>
      filteredNews.map((item) => ({
        image: (
          <Avatar
            src={item.image || "/default-news.png"}
            alt={item.title}
            sx={{
              width: 50,
              height: 50,
              borderRadius: "12px",
              mx: "auto",
              transition: "transform 0.3s",
              "&:hover": { transform: "scale(1.1)" },
            }}
          >
            {!item.image && <NewsIcon />}
          </Avatar>
        ),
        title: (
          <MDTypography variant="button" fontWeight="bold" color="dark">
            {item.title?.length > 30 ? `${item.title.substring(0, 30)}...` : item.title}
          </MDTypography>
        ),
        sort_description: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {item.sort_description?.length > 50
              ? `${item.sort_description.substring(0, 50)}...`
              : item.sort_description}
          </MDTypography>
        ),
        author: (
          <MDTypography variant="caption" fontWeight="medium">
            {item.author || "N/A"}
          </MDTypography>
        ),
        reference_by: (
          <MDTypography variant="caption" fontWeight="medium">
            {item.reference_by || "N/A"}
          </MDTypography>
        ),
        status: <MDBox textAlign="center">{getStatusChip(item.status)}</MDBox>,
        createdAt: (
          <MDTypography variant="caption" fontWeight="medium" textAlign="center">
            {formatDate(item.createdAt)}
          </MDTypography>
        ),
        action: (
          <MDBox display="flex" justifyContent="center" gap={0.5}>
            <Tooltip title="View Details">
              <IconButton
                color="default"
                onClick={() => navigate(`/news-details/${item._id}`)}
                size="small"
                sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit News">
              <IconButton
                color="info"
                onClick={() => handleOpenDialog("edit", item)}
                size="small"
                sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete News">
              <IconButton
                color="error"
                onClick={() => handleOpenDeleteDialog(item._id, item.title)}
                size="small"
                sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      })),
    [filteredNews]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3} sx={{ animation: `${slideUp} 0.5s ease-out` }}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "24px" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <NewsIcon sx={{ color: "#fff", fontSize: 28 }} />
                  <MDTypography variant="h6" color="white">
                    News Management
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="dark"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog("add")}
                  sx={{ borderRadius: "12px", textTransform: "none" }}
                >
                  Add News
                </MDButton>
              </MDBox>

              {/* Filter Section */}
              <Box sx={{ px: 3, pt: 3 }}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Search by Title"
                      value={filterTitle}
                      onChange={(e) => setFilterTitle(e.target.value)}
                      placeholder="Enter news title..."
                      variant="outlined"
                      size="small"
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        label="Status"
                        sx={{ borderRadius: "10px", height: "40px" }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={clearFilters}
                      startIcon={<ClearIcon />}
                      fullWidth
                      sx={{ borderRadius: "10px", height: "40px" }}
                    >
                      Clear
                    </MDButton>
                  </Grid>
                </Grid>
              </Box>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : filteredNews.length === 0 ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <MDTypography variant="h6" color="dark" fontWeight="medium">
                      No news found. Add your first news.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ADD/EDIT DIALOG */}
      <Dialog
        open={openForm}
        onClose={() => !isSubmitting && setOpenForm(false)}
        fullWidth
        maxWidth="md"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #2196f3, #64b5f6, #2196f3)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setOpenForm(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(33, 150, 243, 0.1)",
            },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(33,150,243,0.08) 0%, rgba(33,150,243,0) 70%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        <DialogTitle sx={{ pb: 1, pt: 3, position: "relative", zIndex: 1 }}>
          <Grow in={openForm} timeout={600}>
            <MDBox display="flex" alignItems="center" gap={2}>
              <Zoom in={openForm} timeout={600}>
                <MDBox
                  sx={{
                    background: "linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)",
                    width: 56,
                    height: 56,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 20px rgba(33, 150, 243, 0.3)",
                    animation: `${glow} 2s ease-in-out infinite`,
                  }}
                >
                  {formMode === "add" ? (
                    <AddIcon sx={{ fontSize: 32, color: "white" }} />
                  ) : (
                    <UpdateIcon sx={{ fontSize: 32, color: "white" }} />
                  )}
                </MDBox>
              </Zoom>
              <MDBox>
                <MDTypography
                  variant="h4"
                  fontWeight="bold"
                  sx={{
                    background: "linear-gradient(135deg, #2196f3 0%, #1565c0 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {formMode === "add" ? "Add News" : "Update News"}
                </MDTypography>
                <MDTypography variant="body2" color="textSecondary">
                  {formMode === "add" ? "Create a new news article" : "Edit existing news details"}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grow>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={openForm} timeout={800}>
            <MDBox pt={3} pb={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "100ms" }}>
                    <TextField
                      fullWidth
                      label="Title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter news title"
                      variant="outlined"
                      required
                      InputProps={{
                        startAdornment: <TitleIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "150ms" }}>
                    <TextField
                      fullWidth
                      label="Reference By"
                      name="reference_by"
                      value={formData.reference_by}
                      onChange={handleInputChange}
                      placeholder="Reference source"
                      variant="outlined"
                      InputProps={{ startAdornment: <LinkIcon sx={{ color: "#9e9e9e", mr: 1 }} /> }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "200ms" }}>
                    <TextField
                      fullWidth
                      label="Short Description"
                      name="sort_description"
                      value={formData.sort_description}
                      onChange={handleInputChange}
                      placeholder="Enter short description"
                      multiline
                      rows={2}
                      variant="outlined"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "250ms" }}>
                    <TextField
                      fullWidth
                      label="Full Description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Enter full description"
                      multiline
                      rows={4}
                      variant="outlined"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "300ms" }}>
                    <TextField
                      fullWidth
                      label="Author"
                      name="author"
                      value={formData.author}
                      onChange={handleInputChange}
                      placeholder="Author name"
                      variant="outlined"
                      InputProps={{
                        startAdornment: <PersonIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "350ms" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.status === "active"}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              status: e.target.checked ? "active" : "inactive",
                            }))
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#4caf50",
                            },
                          }}
                        />
                      }
                      label={
                        <MDTypography variant="button" fontWeight="medium">
                          {formData.status === "active" ? "Active" : "Inactive"}
                        </MDTypography>
                      }
                      sx={{ mt: 1 }}
                    />
                  </Zoom>
                </Grid>

                {/* Image Upload */}
                <Grid item xs={12}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "400ms" }}>
                    <Box>
                      <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                        News Image
                      </MDTypography>
                      <Paper
                        elevation={0}
                        sx={{
                          border: "2px dashed #e0e0e0",
                          borderRadius: "16px",
                          p: 2,
                          textAlign: "center",
                          bgcolor: "#fafafa",
                          cursor: "pointer",
                          transition: "all 0.3s",
                          "&:hover": {
                            borderColor: "#2196f3",
                            bgcolor: "#f5f5f5",
                            transform: "scale(1.02)",
                          },
                        }}
                        onClick={() => document.getElementById("image-upload").click()}
                      >
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          style={{ display: "none" }}
                        />
                        {imagePreview ? (
                          <Box sx={{ animation: `${bounceIn} 0.5s ease-out` }}>
                            <img
                              src={imagePreview}
                              alt="Preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "200px",
                                objectFit: "contain",
                                borderRadius: "12px",
                                marginBottom: "10px",
                              }}
                            />
                            <Box display="flex" justifyContent="center" gap={2}>
                              <MDButton
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveImage();
                                }}
                                startIcon={<DeleteOutlineIcon />}
                                sx={{ borderRadius: "10px" }}
                              >
                                Remove
                              </MDButton>
                              <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  document.getElementById("image-upload").click();
                                }}
                                startIcon={<CloudUploadIcon />}
                                sx={{ borderRadius: "10px" }}
                              >
                                Change
                              </MDButton>
                            </Box>
                          </Box>
                        ) : (
                          <Box>
                            <CloudUploadIcon sx={{ fontSize: 48, color: "#9e9e9e", mb: 1 }} />
                            <MDTypography variant="body2" color="text">
                              Click to upload or drag and drop
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              PNG, JPG, JPEG, WEBP (Max 2MB)
                            </MDTypography>
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  </Zoom>
                </Grid>
              </Grid>
            </MDBox>
          </Fade>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            gap: 2,
            justifyContent: "flex-end",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={openForm} timeout={600} style={{ transitionDelay: "450ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => {
                setOpenForm(false);
                resetForm();
              }}
              disabled={isSubmitting}
              sx={{
                borderRadius: "12px",
                px: 3,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                color: "#111",
                borderColor: "#111",
                "&:hover": { transform: "translateY(-2px)", borderColor: "#111" },
              }}
            >
              Cancel
            </MDButton>
          </Zoom>
          <Zoom in={openForm} timeout={600} style={{ transitionDelay: "500ms" }}>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                borderRadius: "12px",
                px: 4,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
              }
            >
              {isSubmitting ? "PROCESSING..." : formMode === "add" ? "SAVE" : "UPDATE"}
            </MDButton>
          </Zoom>
        </DialogActions>
      </Dialog>

      {/* VIEW NEWS DIALOG */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        fullWidth
        maxWidth="md"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
          },
        }}
      >
        <IconButton
          onClick={() => setViewDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(33, 150, 243, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ pb: 1, pt: 3, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Zoom in={viewDialogOpen} timeout={600}>
            <MDBox display="flex" flexDirection="column" alignItems="center" gap={2}>
              <MDBox
                sx={{
                  background: "linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 20px rgba(33, 150, 243, 0.3)",
                  animation: `${float} 2s ease-in-out infinite`,
                }}
              >
                <NewsIcon sx={{ fontSize: 45, color: "white" }} />
              </MDBox>
              <MDTypography variant="h4" fontWeight="bold">
                {selectedNews?.title}
              </MDTypography>
              {selectedNews && getStatusChip(selectedNews.status)}
            </MDBox>
          </Zoom>
        </DialogTitle>
        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={viewDialogOpen} timeout={800}>
            <Box sx={{ py: 2 }}>
              <Grid container spacing={3}>
                {selectedNews?.image && (
                  <Grid item xs={12}>
                    <Paper
                      sx={{ p: 2, borderRadius: "16px", textAlign: "center", bgcolor: "#f8f9fa" }}
                    >
                      <img
                        src={selectedNews.image}
                        alt={selectedNews.title}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "300px",
                          objectFit: "contain",
                          borderRadius: "12px",
                        }}
                      />
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#e3f2fd" }}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      SHORT DESCRIPTION
                    </MDTypography>
                    <MDTypography variant="body1">{selectedNews?.sort_description}</MDTypography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#f8f9fa" }}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      FULL DESCRIPTION
                    </MDTypography>
                    <MDTypography variant="body2" lineHeight={1.8}>
                      {selectedNews?.description}
                    </MDTypography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      bgcolor: "#f8f9fa",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <PersonIcon sx={{ color: metaBlue }} />
                    <Box>
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        Author
                      </MDTypography>
                      <MDTypography variant="body2">{selectedNews?.author || "Admin"}</MDTypography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      bgcolor: "#f8f9fa",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <LinkIcon sx={{ color: metaBlue }} />
                    <Box>
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        Reference By
                      </MDTypography>
                      <MDTypography variant="body2">
                        {selectedNews?.reference_by || "N/A"}
                      </MDTypography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#f8f9fa" }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Created At
                        </MDTypography>
                        <MDTypography variant="body2">
                          {formatDate(selectedNews?.createdAt)}
                        </MDTypography>
                      </Grid>
                      <Grid item xs={6}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Last Updated
                        </MDTypography>
                        <MDTypography variant="body2">
                          {formatDate(selectedNews?.updatedAt)}
                        </MDTypography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center" }}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => setViewDialogOpen(false)}
            sx={{ borderRadius: "12px", px: 4 }}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isSubmitting && setDeleteDialogOpen(false)}
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "32px",
            background: "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setDeleteDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": { transform: "rotate(90deg) scale(1.1)", bgcolor: "rgba(239, 68, 68, 0.1)" },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon sx={{ color: "#ef4444" }} />
        </IconButton>
        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0) 70%)",
            pointerEvents: "none",
          }}
        />
        <DialogTitle sx={{ pt: 4, pb: 2, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Grow in={deleteDialogOpen} timeout={600}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Zoom in={deleteDialogOpen} timeout={600}>
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    borderRadius: "50%",
                    animation: `${pulseRing} 2s infinite`,
                    cursor: "pointer",
                    mb: 2,
                    "&:hover": { animation: `${shakeAnimation} 0.5s ease-in-out` },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(239, 68, 68, 0.4)",
                      animation: `${glow} 2s ease-in-out infinite`,
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 40, color: "white" }} />
                  </Box>
                </Box>
              </Zoom>
              <Box sx={{ animation: `${bounceIn} 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)` }}>
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold">
                    Delete News?
                  </MDTypography>
                </Box>
              </Box>
            </Box>
          </Grow>
        </DialogTitle>
        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={deleteDialogOpen} timeout={800}>
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "100ms" }}>
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{
                    borderRadius: "16px",
                    mb: 3,
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                  }}
                >
                  <MDTypography variant="body2" fontWeight="medium" color="error">
                    This action cannot be undone!
                  </MDTypography>
                </Alert>
              </Zoom>
              <Grow in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "150ms" }}>
                <Paper
                  elevation={0}
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(239,68,68,0.05) 0%, rgba(239,68,68,0.02) 100%)",
                    borderRadius: "20px",
                    p: 3,
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "scale(1.02)", borderColor: "rgba(239, 68, 68, 0.4)" },
                  }}
                >
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    fontWeight="bold"
                  >
                    NEWS TO BE DELETED
                  </MDTypography>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#ef4444", wordBreak: "break-word" }}
                  >
                    &quot;{deleteNewsTitle}&quot;
                  </MDTypography>
                </Paper>
              </Grow>
              <Fade in={deleteDialogOpen} timeout={800} style={{ transitionDelay: "200ms" }}>
                <Box sx={{ mt: 3 }}>
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                    This news will be permanently removed from the system
                  </MDTypography>
                </Box>
              </Fade>
            </Box>
          </Fade>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 4,
            pt: 2,
            gap: 2,
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "250ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                color: "#111",
                borderColor: "#111",
                "&:hover": { transform: "translateY(-2px)", borderColor: "#111" },
              }}
            >
              Cancel
            </MDButton>
          </Zoom>
          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "300ms" }}>
            <MDButton
              variant="contained"
              color="error"
              onClick={handleDeleteNews}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />
              }
            >
              {isSubmitting ? "DELETING..." : "DELETE PERMANENTLY"}
            </MDButton>
          </Zoom>
        </DialogActions>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
            animation: `${shimmer} 2s infinite linear`,
            backgroundSize: "200% 100%",
          }}
        />
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Slide}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default NewsManagement;
